import { Injectable } from '@angular/core';
import {CircleGeometry, Color, Geometry, Line, Line3, LineBasicMaterial, Mesh, MeshBasicMaterial, Object3D, Plane, Vector3} from 'three';
import {RenderService} from '../Scene/render.service';
import {MouseControlsService} from '../Control/mouse-controls.service';
import {ContainerService} from '../../Utils/container.service';
import {SceneService} from '../Scene/scene.service';
import {CameraService} from '../Scene/camera.service';

@Injectable({
  providedIn: 'root'
})
export class PaintService {

    // get/set
    private _rawpoints: Vector3[] = [];
    // get
    public _points: Vector3[] = [];

    private _drawIndex = 0;
    private _plane: Plane;

    private _paintColor = {name: 'default', color: new Color(0x668fff)};

    constructor(private sceneService: SceneService,
                private cameraService: CameraService,
                private containerService: ContainerService,
                private renderService: RenderService,
                private mouseService: MouseControlsService) { }


    get paintColor(): { name: string, color: Color } {
        return this._paintColor;
    }

    set paintColor(value: { name: string, color: Color }) {
        this._paintColor = value;
    }

    get rawpoints(): Array<Vector3> {
        return this._rawpoints;
    }

    set rawpoints(value: Vector3[]) {
        this._rawpoints = value;
    }

    get points(): Vector3[] {
        return this._points;
    }

    get plane(): Plane {
        return this._plane;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    // event controls ---------------

    public startPaintFromEvent(event: MouseEvent, pen: boolean = false, penSize: number = 0) {
        if (event.buttons !== 2 && this.mouseService.multitouch === 1 && this.containerService.loadingStatus === 1) {
            this.startPaint();
            const result = this.get3dPointOnPlane(event);
            this.drawToScene(true, pen, penSize);
            this.renderService.render();
            return result;
        }
        return null;
    }

    public paintForEvent(event: MouseEvent, pen: boolean = false, penSize: number = 0) {
        if (event.buttons === 1 && this.mouseService.multitouch === 1 && this.containerService.loadingStatus === 1) {
            // this.changeDrawMode(true);
            const result = this.get3dPointOnPlane(event);
            this.drawToScene(true, pen, penSize);
            return result;
        } else {
            this.clearLassoPoints();
            this.renderService.render();
        }
    }

    // --------------------------

    /**
     * starts lasso/paint mode
     */
    public startPaint() {
        this.computeSelectionPlane();
        if (!this.sceneService.sceneContainsName('Lasso')) {
            this.containerService.lassoSelectionObject = new Object3D();
            this.containerService.lassoSelectionObject.name = 'Lasso';
            this.sceneService.addObject(this.containerService.lassoSelectionObject);
        }
        // this.sceneService.addGroup('Lasso');
    }

    /**
     * calculates selection plane
     * this plane is used to draw lasso selection or brush paint
     */
    private computeSelectionPlane() {
        // compute the selection plane
        const camDirVec = new Vector3(0, 0, 0);
        this.cameraService.camera.getWorldDirection(camDirVec).normalize();
        const ptCamOnPlane = this.cameraService.camera.position.clone().add(camDirVec.clone().normalize().multiplyScalar(3));

        // helper plane through origin
        const plane = new Plane(camDirVec, 0);

        // distance to desired plane pt
        const dist = plane.distanceToPoint(ptCamOnPlane);
        this._plane = new Plane(camDirVec, -dist);
    }


    /**
     * calculate Point on Plane and add ClickPoint to selectionPoly
     * @param event
     */
    private get3dPointOnPlane(event) {
        return this.get3dPointOnPlaneByOffset(event.offsetX, event.offsetY);
    }

    /**
     * calculate Point on Plane and add ClickPoint to selectionPoly depending on Offset
     * @param offsetX
     * @param offsetY
     */
    private get3dPointOnPlaneByOffset(offsetX: number, offsetY: number) {
        const vector = new Vector3(
            ((offsetX / this.cameraService.canvasWidth) * 2) - 1,
            -(offsetY / this.cameraService.canvasHeight) * 2 + 1,
            0.5);
        const p = new Vector3(-(this.cameraService.canvasWidth / 2) + offsetX,
            (this.cameraService.canvasHeight / 2) - offsetY, 0.0);

        this.addRawPoint(p);

        vector.unproject(this.cameraService.camera);
        const pt = vector.clone();
        const dir = vector.sub(this.cameraService.camera.position).normalize();

        const point = this.intersectLine(pt, dir);
        this._points.push(point);

        return {point: point, rawpoint: p, direction: dir};
    }

    public addRawPoint(point: Vector3) {
        if (this._rawpoints.indexOf(point) < 0) {
            this._rawpoints.push(point);
        }
    }

    /**
     * returns intersection of line
     * @param point
     * @param direction
     */
    private intersectLine(point: Vector3, direction: Vector3) {
        const lineStart = point.clone().sub(direction.clone().normalize().multiplyScalar(1000));
        const lineEnd = point.clone().add(direction.clone().normalize().multiplyScalar(1000));
        const linePointCam = new Line3(lineStart, lineEnd);

        return this._plane.intersectLine(linePointCam, new Vector3(100, 100, 100));
    }

    /**
     * draw brush selection to scene
     * @param visible
     * @param pen
     * @param penSize
     */
    public drawToScene(visible: boolean, pen: boolean = false, penSize = 0) {
        if (this._drawIndex < (this._points.length - 1)) {
            for (let i = this._drawIndex; i < (this._points.length - 1); i++) {
                if (pen) {
                    this.addPenLine(penSize, this._points[i], this._points[i + 1], visible);
                } else {
                    this.addLineSegment(this._points[i], this._points[i + 1], visible);
                }
            }
        } else {
            if (pen) {
                this.addPenLine(penSize, this._points[0], this._points[0], visible);
            } else {
                this.addLineSegment(this._points[0], this._points[0], visible);
            }
            this._drawIndex = 1;
        }

        this._drawIndex = this._points.length - 1;
        this.renderService.render();
    }

    /**
    * adds line segment (brush drawing) and draws it to the render scene
    * @param from
    * @param to
    * @param visible
    */
    public addLineSegment(from: Vector3, to: Vector3, visible: boolean) {
        const material = new LineBasicMaterial({color: 0x00ff00, linewidth: 5, linecap: 'round'});
        if (!visible) {
            material.transparent = true;
        }
        const geometry = new Geometry();
        geometry.vertices.push(from);
        geometry.vertices.push(to);

        const line = new Line(geometry, material);
        line.name = 'test';

        this.sceneService.getGroupByName('Lasso').add(line);
    }

    /**
     * adds pen segment (brush drawing) and draws it to the render scene
     * @param size
     * @param from
     * @param to
     * @param visible
     * @param opacity
     */
    public addPenLine(size, from, to, visible: boolean, opacity = 0.8) {
        const geometry = new CircleGeometry(size, 32);
        const material = new MeshBasicMaterial({color: this._paintColor.color});

        const group = this.sceneService.getGroupByName('Lasso');
        if (!visible) {
            material.transparent = true;
        }
        material.opacity = opacity;

        const dist = from.clone().distanceTo(to.clone());
        const dir = to.clone().sub(from);
        const pendist = size / 10;
        if (dist !== 0) {
            for (let i = 0; i < dist; i += pendist) {
                const point = from.clone().add(dir.clone().multiplyScalar(i));
                const line = new Mesh(geometry, material);
                line.position.set(point.x, point.y, point.z);
                line.name = 'test';
                line.rotation.setFromRotationMatrix(this.cameraService.camera.matrix);
                group.add(line);
            }
        } else {
            const line = new Mesh(geometry, material);
            line.position.set(from.x, from.y, from.z);
            line.name = 'test';
            line.rotation.setFromRotationMatrix(this.cameraService.camera.matrix);
            group.add(line);
        }

    }

    /**
     * clears lasso points from render scene
     */
    public clearLassoPoints() {
        this._drawIndex = 0;
        this._points = [];
        this._rawpoints = [];

        this.sceneService.removeGroup('Lasso');
        this.renderService.render();
    }

    public closePoly() {
        if (this._points.length > 2) {
            this._points.push(this._points[0]);
            this._rawpoints.push(this._rawpoints[0]);
        }
    }
}
